import { Tag, Tooltip, type TableColumnsType, Typography } from "antd"
import dayjs from "dayjs"
import type { TFunction } from "react-i18next"

import {
  SLA_BUSINESS_DAYS,
  STATUS_COLOR_MAP
} from "../../redux/account-deletion.constants.ts"
import type { AccountDeletionRequest } from "../../types/types.ts"

const { Text } = Typography

type FormatDate = (
  date: string | Date | null | undefined,
  format?: string
) => string | Date | null | undefined

/** `formatDate` echoes its input back when falsy, hence the widened return. */
const renderDate =
  (formatDate: FormatDate) =>
  (value?: string): string =>
    value ? String(formatDate(value)) : "—"

/**
 * Mirrors `apps/user/account_deletion_constants.business_days_since`, which
 * counts over UTC dates — hence `dayjs.utc`, not the viewer's timezone.
 */
export const businessDaysSince = (
  iso: string,
  now: Date = new Date()
): number => {
  let start = dayjs.utc(iso).startOf("day")
  const end = dayjs.utc(now).startOf("day")
  let count = 0
  while (start.isBefore(end)) {
    start = start.add(1, "day")
    const dow = start.day()
    if (dow !== 0 && dow !== 6) count += 1
  }
  return count
}

const slaColor = (days: number, escalated: boolean): string => {
  if (escalated || days > SLA_BUSINESS_DAYS.finalTarget) return "red"
  if (days > SLA_BUSINESS_DAYS.initialReview) return "gold"
  return "green"
}

export const getColumns = (
  t: TFunction,
  formatDate: FormatDate
): TableColumnsType<AccountDeletionRequest> => [
  {
    title: t("accountDeletionRequests.columns.requester"),
    key: "requester",
    render: (_, r) => (
      <div>
        <div>{r.user.name}</div>
        <Text type="secondary">{r.user.email}</Text>
      </div>
    )
  },
  {
    title: t("accountDeletionRequests.columns.reason"),
    key: "reason",
    render: (_, r) => (
      <Tooltip title={r.reason_text ?? ""}>
        {t(`accountDeletionRequests.reasons.${r.reason_id}`)}
      </Tooltip>
    )
  },
  {
    title: t("accountDeletionRequests.columns.status"),
    dataIndex: "status",
    key: "status",
    render: (_, r) => (
      <Tag color={STATUS_COLOR_MAP[r.status]}>
        {t(`accountDeletionRequests.statuses.${r.status}`)}
      </Tag>
    )
  },
  {
    title: t("accountDeletionRequests.columns.requested"),
    dataIndex: "created_at",
    key: "created_at",
    render: renderDate(formatDate)
  },
  {
    title: t("accountDeletionRequests.columns.sla"),
    key: "sla",
    render: (_, r) => {
      if (r.status !== "pending") return "—"
      const days = businessDaysSince(r.created_at)
      const escalated = !!r.escalation_reminded_at
      const label =
        days === 0
          ? t("accountDeletionRequests.sla.today")
          : t("accountDeletionRequests.sla.days", { count: days })
      return (
        <Tooltip
          title={t("accountDeletionRequests.sla.tooltip", {
            target: SLA_BUSINESS_DAYS.initialReview,
            overdue: SLA_BUSINESS_DAYS.finalTarget
          })}
        >
          <Tag color={slaColor(days, escalated)}>
            {escalated
              ? `${label} · ${t("accountDeletionRequests.sla.escalated")}`
              : label}
          </Tag>
        </Tooltip>
      )
    }
  },
  {
    title: t("accountDeletionRequests.columns.decided"),
    dataIndex: "decided_at",
    key: "decided_at",
    render: renderDate(formatDate)
  }
]
